import React, { createContext, useContext, useMemo, useCallback } from 'react';
import useSWR, { SWRConfig, useSWRConfig } from 'swr';

const RARIBLE_MATCH_RE = /^https:\/\/rarible\.com\/token\/(0x[a-fA-F0-9]{40}):([0-9]+)/;
function isAddress(value) {
    return /^0x[a-fA-F0-9]{40}$/.test(value);
}
function identity(arg) {
    return arg;
}
function parseNftUrl(url) {
    const raribleMatch = RARIBLE_MATCH_RE.exec(url);
    if (raribleMatch) {
        return [raribleMatch[1], raribleMatch[2]];
    }
    return null;
}
function fetchImage(src) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.src = src;
        image.crossOrigin = "";
        image.onload = () => resolve(image);
        image.onerror = (error) => reject(error);
    });
}
// Scale the image and add some extra padding. Returns the image as base64.
// The padding and scale are expressed as proportions of the image size.
function frameImage(image, { scale = 1, padding = 0 } = {}) {
    const width = image.naturalWidth * scale;
    const height = image.naturalHeight * scale;
    const _padding = Math.max(width * padding, height * padding);
    const canvas = document.createElement("canvas");
    canvas.width = width + _padding * 2;
    canvas.height = height + _padding * 2;
    const ctx = canvas.getContext("2d");
    if (ctx === null) {
        return null;
    }
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(image, _padding, _padding, width, height);
    return canvas.toDataURL();
}
function ipfsUrlDefault(cid, path = "") {
    return `https://ipfs.io/ipfs/${cid}${path}`;
}
const IPFS_PROTOCOL_RE = /^ipfs:\/\/(?:ipfs\/)?([^/]+)(\/.+)?$/;
const IPFS_HASH_RE = /^Qm[1-9A-HJ-NP-Za-km-z]{44}$/;
function ipfsUrlFromString(ipfsString, ipfsUrl) {
    // ipfs:// URI
    const ipfsProtocolMatch = IPFS_PROTOCOL_RE.exec(ipfsString);
    if (ipfsProtocolMatch) {
        const [, cid, path = ""] = ipfsProtocolMatch;
        return ipfsUrl(cid, path);
    }
    // standalone cid, probably
    if (IPFS_HASH_RE.test(ipfsString)) {
        return ipfsUrl(ipfsString);
    }
    // maybe URL
    return ipfsString;
}
function normalizeOpenSeaUrl(url, tokenId) {
    // url can be anything so we need to try / catch to pass it to new URL()
    try {
        const _url = new URL(url);
        // 0x%7Bid%7D" = 0x{id} (url encoded)
        if ((_url.host !== "api.opensea.io" &&
            _url.host !== "testnets-api.opensea.io") ||
            !_url.pathname.includes("0x%7Bid%7D")) {
            return url;
        }
        _url.pathname = _url.pathname.replace(/0x%7Bid%7D/g, tokenId);
        _url.searchParams.set("format", "json");
        return String(_url);
    }
    catch (err) {
        return url;
    }
}
function normalizeNiftyGatewayUrl(url) {
    try {
        const _url = new URL(url);
        if (_url.host !== "api.niftygateway.com") {
            return url;
        }
        // Without final slash, the Nifty Gateway API server
        // doesn’t set the CORS headers properly.
        _url.pathname = _url.pathname + "/";
        return String(_url);
    }
    catch (err) {
        return url;
    }
}
function normalizeTokenUrl(url, tokenId, fetchContext) {
    url = normalizeOpenSeaUrl(url, tokenId);
    url = normalizeNiftyGatewayUrl(url);
    url = ipfsUrlFromString(url, fetchContext.ipfsUrl);
    if (url.startsWith("http")) {
        url = fetchContext.jsonProxy(url);
    }
    return url;
}
function normalizeImageUrl(url, fetchContext) {
    return ipfsUrlFromString(url, fetchContext.ipfsUrl);
}
function normalizeNftMetadata(data, fetchContext) {
    return {
        ...data,
        image: normalizeImageUrl(data.image, fetchContext),
    };
}
function fixIncorrectImageField(data) {
    if (!data || typeof data !== "object") {
        return data;
    }
    const _data = data;
    // makersplace.com is using `imageUrl` rather than `image`
    if (typeof (_data === null || _data === void 0 ? void 0 : _data.image) === "undefined" &&
        typeof (_data === null || _data === void 0 ? void 0 : _data.imageUrl) === "string") {
        return { ..._data, image: _data === null || _data === void 0 ? void 0 : _data.imageUrl };
    }
    return data;
}
// See NftMetadataMixedInJsonSchema for why this is needed.
function isNftMetadataMixedInJsonSchema(data) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    if (!data || typeof data !== "object") {
        return false;
    }
    const _data = data;
    return (_data.title === "Asset Metadata" &&
        _data.type === "object" &&
        typeof ((_b = (_a = _data.properties) === null || _a === void 0 ? void 0 : _a.name) === null || _b === void 0 ? void 0 : _b.description) === "string" &&
        typeof ((_d = (_c = _data.properties) === null || _c === void 0 ? void 0 : _c.image) === null || _d === void 0 ? void 0 : _d.description) === "string" &&
        typeof ((_f = (_e = _data.properties) === null || _e === void 0 ? void 0 : _e.description) === null || _f === void 0 ? void 0 : _f.description) === "string" &&
        ((_h = (_g = _data.properties) === null || _g === void 0 ? void 0 : _g.name) === null || _h === void 0 ? void 0 : _h.type) === "string" &&
        ((_k = (_j = _data.properties) === null || _j === void 0 ? void 0 : _j.image) === null || _k === void 0 ? void 0 : _k.type) === "string" &&
        ((_m = (_l = _data.properties) === null || _l === void 0 ? void 0 : _l.description) === null || _m === void 0 ? void 0 : _m.type) === "string");
}
function fixNftMetadataMixedInJsonSchema(data) {
    var _a, _b, _c, _d, _e, _f;
    return {
        name: ((_b = (_a = data.properties) === null || _a === void 0 ? void 0 : _a.name) === null || _b === void 0 ? void 0 : _b.description) || "",
        description: ((_d = (_c = data.properties) === null || _c === void 0 ? void 0 : _c.description) === null || _d === void 0 ? void 0 : _d.description) || "",
        image: ((_f = (_e = data.properties) === null || _e === void 0 ? void 0 : _e.image) === null || _f === void 0 ? void 0 : _f.description) || "",
        rawData: { ...data },
    };
}
function isNftMetadata(data) {
    if (!data || typeof data !== "object") {
        return false;
    }
    const _data = data;
    // We don’t test for the exact type here, because some
    // NFT minting services set some of the fields as null.
    return "name" in _data && "image" in _data && "description" in _data;
}
function addressesEqual(addr1, addr2) {
    return (addr1 === null || addr1 === void 0 ? void 0 : addr1.toLowerCase()) === (addr2 === null || addr2 === void 0 ? void 0 : addr2.toLowerCase());
}
// Promise.any() implementation from https://github.com/m0ppers/promise-any
function promiseAny(promises) {
    return reversePromise(Promise.all([...promises].map(reversePromise)));
}
function reversePromise(promise) {
    return new Promise((resolve, reject) => {
        Promise.resolve(promise).then(reject, resolve);
    });
}
// To replace with AggregateError when useNft() will target ES2021 environments
class MultipleErrors extends Error {
    constructor(message, errors) {
        super(message);
        this.name = "MultipleErrors";
        this.errors = errors;
    }
}
const IMAGE_EXT_RE = /\.(?:png|svg|jpg|jepg|gif|webp|jxl|avif)$/;
const VIDEO_EXT_RE = /\.(?:mp4|mov|webm|ogv)$/;
// Guess a file type from the extension used in a URL
function urlExtensionType(url) {
    if (IMAGE_EXT_RE.test(url))
        return "image";
    if (VIDEO_EXT_RE.test(url))
        return "video";
    return "unknown";
}

const CRYPTOKITTIES = "0x06012c8cf97BEaD5deAe237070F9587f8E7A266d";
const CRYPTOPUNKS = "0xb47e3cd837ddf8e4c57f05d70ab865de6e193bbb";
const CRYPTOPUNKS_IMAGES = "0x16F5A35647D6F03D5D3da7b35409D65ba03aF3B2";
const DECENTRALAND_ESTATE = "0x959e104E1a4dB6317fA58F8295F586e1A978c297";
const DECENTRALAND_PARCEL = "0xf87e31492faf9a91b02ee0deaad50d51d56d5d4d";

const CRYPTOPUNKS_DESCRIPTION = `
  10,000 unique collectible characters with proof of ownership stored on the
  Ethereum blockchain. The project that inspired the modern CryptoArt movement.
  The first "Non-Fungible Token," and inspiration for the Ethereum ERC-721
  standard that powers most digital art and collectibles.
`;
const CRYPTOPUNKS_IMAGE_SVG = {
    address: CRYPTOPUNKS_IMAGES,
    methodName: "punkImageSvg",
    methodHash: "0x74beb047",
    humanReadableAbi: [
        "function punkImageSvg(uint16 index) view returns (string svg)",
    ],
};
function encodeUriData(dataUri) {
    var _a;
    const dataStart = dataUri.indexOf(",") + 1;
    return ((_a = dataUri.slice(0, dataStart) +
        encodeURIComponent(dataUri.slice(dataStart))) !== null && _a !== void 0 ? _a : "");
}
async function cryptoPunksMetadata(index, cryptoPunksImage) {
    const image = await cryptoPunksImage(index, CRYPTOPUNKS_IMAGE_SVG);
    return {
        description: CRYPTOPUNKS_DESCRIPTION,
        image: encodeUriData(image),
        imageType: "image",
        metadataUrl: "",
        name: `CryptoPunk ${index}`,
        owner: "",
        rawData: null,
    };
}
function isCryptoPunks(contractAddress) {
    return addressesEqual(contractAddress, CRYPTOPUNKS);
}

async function cryptoKittiesMetadata(id, { jsonProxy }) {
    var _a, _b, _c;
    const metadataUrl = jsonProxy(`https://api.cryptokitties.co/v3/kitties/${id}`);
    const res = await fetch(metadataUrl);
    const data = (await res.json());
    const image = (_a = data === null || data === void 0 ? void 0 : data.image_url) !== null && _a !== void 0 ? _a : "";
    return {
        description: (_b = data === null || data === void 0 ? void 0 : data.bio) !== null && _b !== void 0 ? _b : "−",
        image,
        imageType: image ? "image" : "unknown",
        metadataUrl,
        name: (_c = data === null || data === void 0 ? void 0 : data.name) !== null && _c !== void 0 ? _c : "Unknown",
        owner: "",
        rawData: data,
    };
}
function isCryptoKitties(contractAddress) {
    return addressesEqual(contractAddress, CRYPTOKITTIES);
}

const ENDPOINT$1 = "https://api.thegraph.com/subgraphs/name/decentraland/marketplace";
const QUERY$1 = `
  query NFTByTokenId($contractAddress: String, $tokenId: String) {
    nfts(
      where: { contractAddress: $contractAddress, tokenId: $tokenId }
      first: 1
    ) {
      name
      image
      owner {
        address
      }
      estate {
        size
        data {
          description
        }
      }
    }
  }
`;
function body$1(contractAddress, tokenId) {
    return JSON.stringify({
        operationName: "NFTByTokenId",
        variables: { contractAddress, tokenId },
        query: QUERY$1,
    });
}
async function decentralandEstateMetadata(tokenId) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const response = await fetch(ENDPOINT$1, {
        body: body$1(DECENTRALAND_ESTATE, tokenId),
        method: "POST",
    });
    const { data } = (await response.json());
    const nft = (_a = data === null || data === void 0 ? void 0 : data.nfts) === null || _a === void 0 ? void 0 : _a[0];
    const image = (_b = nft === null || nft === void 0 ? void 0 : nft.image) !== null && _b !== void 0 ? _b : "";
    return {
        description: (_e = (_d = (_c = nft === null || nft === void 0 ? void 0 : nft.estate) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.description) !== null && _e !== void 0 ? _e : "−",
        image,
        imageType: image ? "image" : "unknown",
        metadataUrl: "",
        name: (_f = nft === null || nft === void 0 ? void 0 : nft.name) !== null && _f !== void 0 ? _f : "Unknown",
        owner: (_h = (_g = nft === null || nft === void 0 ? void 0 : nft.owner) === null || _g === void 0 ? void 0 : _g.address) !== null && _h !== void 0 ? _h : "",
        rawData: data,
    };
}
function isDecentralandEstate(contractAddress) {
    return addressesEqual(contractAddress, DECENTRALAND_ESTATE);
}

const ENDPOINT = "https://api.thegraph.com/subgraphs/name/decentraland/marketplace";
const QUERY = `
  query NFTByTokenId($contractAddress: String, $tokenId: String) {
    nfts(
      where: { contractAddress: $contractAddress, tokenId: $tokenId }
      first: 1
    ) {
      name
      image
      owner {
        address
      }
      parcel {
        x
        y
        data {
          description
        }
      }
    }
  }
`;
function body(contractAddress, tokenId) {
    return JSON.stringify({
        operationName: "NFTByTokenId",
        variables: { contractAddress, tokenId },
        query: QUERY,
    });
}
async function decentralandParcelMetadata(tokenId) {
    var _a, _b, _c, _d, _e, _f, _g;
    const response = await fetch(ENDPOINT, {
        body: body(DECENTRALAND_PARCEL, tokenId),
        method: "POST",
    });
    const { data } = (await response.json());
    const nft = (_a = data === null || data === void 0 ? void 0 : data.nfts) === null || _a === void 0 ? void 0 : _a[0];
    const parcel = nft === null || nft === void 0 ? void 0 : nft.parcel;
    const image = (_b = nft === null || nft === void 0 ? void 0 : nft.image) !== null && _b !== void 0 ? _b : "";
    return {
        description: (_d = (_c = parcel === null || parcel === void 0 ? void 0 : parcel.data) === null || _c === void 0 ? void 0 : _c.description) !== null && _d !== void 0 ? _d : "-",
        image,
        imageType: image ? "image" : "unknown",
        metadataUrl: "",
        name: (_e = nft === null || nft === void 0 ? void 0 : nft.name) !== null && _e !== void 0 ? _e : `Parcel ${parcel === null || parcel === void 0 ? void 0 : parcel.x},${parcel === null || parcel === void 0 ? void 0 : parcel.y}`,
        owner: (_g = (_f = nft === null || nft === void 0 ? void 0 : nft.owner) === null || _f === void 0 ? void 0 : _f.address) !== null && _g !== void 0 ? _g : "",
        rawData: data,
    };
}
function isDecentralandParcel(contractAddress) {
    return addressesEqual(contractAddress, DECENTRALAND_PARCEL);
}

const MOONCATS_WRAPPED = {
    address: "0x7c40c393dc0f283f318791d746d894ddd3693572",
    methodName: "_tokenIDToCatID",
    methodHash: "0xfe294644",
    humanReadableAbi: [
        "function _tokenIDToCatID(uint256 tokenId) view returns (bytes5 catId)",
    ],
};
// See https://www.reddit.com/r/MoonCatRescue/comments/m5d7mx/svg_imagery_of_all_rescued_mooncats/
const MOONCATS_IPFS_CID = "bafybeidk4zunuq56w2pf2sncexohlyqae62dzplljkbwswa7jwywh2dava";
async function imageUrl(catId, ipfsUrl) {
    const dir = catId.slice(4, 6);
    const url = ipfsUrlFromString(`ipfs://ipfs/${MOONCATS_IPFS_CID}/${dir}/${catId}.png`, ipfsUrl);
    const image = await fetchImage(url);
    // Here we increase the resolution of the MoonCats PNG files (4x without
    // antialias) and add a some padding around it. Doing this image manipulation
    // rather than using CSS is needed because useNft() only deals with data and
    // doesn’t have any control over how the images get displayed.
    return frameImage(image, { scale: 4, padding: 0.125 });
}
async function moonCatsMetadata(tokenId, getCatId, fetchContext) {
    var _a;
    const catId = await getCatId(tokenId, MOONCATS_WRAPPED);
    const image = (_a = (await imageUrl(catId, fetchContext.ipfsUrl))) !== null && _a !== void 0 ? _a : "";
    return {
        description: `The (unofficial) wrapped version of MoonCats Rescue. ` +
            `Original cat ID: ${catId}.`,
        image,
        imageType: image ? "image" : "unknown",
        metadataUrl: "",
        name: `Wrapped MoonCat #${tokenId}`,
        owner: "",
        rawData: null,
    };
}
function isMoonCats(contractAddress) {
    return addressesEqual(contractAddress, MOONCATS_WRAPPED.address);
}

function cryptoPunksImage$1(config) {
    return async function cryptoPunksImage(index, method) {
        const contract = new config.ethers.Contract(method.address, method.humanReadableAbi, config.provider);
        return contract.punkImageSvg(index);
    };
}

function moonCatsCatId$1(config) {
    return async function moonCatsCatId(tokenId, method) {
        const wrappedContract = new config.ethers.Contract(method.address, method.humanReadableAbi, config.provider);
        const result = await wrappedContract._tokenIDToCatID(tokenId);
        return result !== null && result !== void 0 ? result : "";
    };
}

async function fetchMetadata(url, fetchContext) {
    const res = await fetch(url);
    if (!res.ok) {
        throw new Error("Error when trying to request " + url);
    }
    let rawData;
    try {
        rawData = (await res.json());
    }
    catch (err) {
        // If it can’t be parsed as JSON, it must be an image URL
        rawData = { name: "", description: "", image: url };
    }
    let data = { ...rawData };
    if (isNftMetadataMixedInJsonSchema(data)) {
        data = fixNftMetadataMixedInJsonSchema(data);
    }
    data = fixIncorrectImageField(data);
    if (!isNftMetadata(data)) {
        throw new Error("Invalid data received");
    }
    return normalizeNftMetadata({
        description: data.description || "",
        image: data.image || "",
        name: data.name || "",
        rawData,
    }, fetchContext);
}

const ABI = [
    // ERC-721
    "function tokenURI(uint256 _tokenId) external view returns (string)",
    "function ownerOf(uint256 _tokenId) external view returns (address)",
    // ERC-1155
    "function uri(uint256 _id) external view returns (string)",
];
async function url$1(contract, tokenId, fetchContext) {
    const uri = await promiseAny([
        contract.tokenURI(tokenId),
        contract.uri(tokenId),
    ]).catch((errors) => {
        throw new MultipleErrors("An error occurred while trying to fetch the token URI from the NFT" +
            " contract. See the “errors” property on this error for details.", errors);
    });
    return normalizeTokenUrl(uri, tokenId, fetchContext);
}
async function fetchStandardNftContractData$1(contractAddress, tokenId, config, fetchContext) {
    const contract = new config.ethers.Contract(contractAddress, ABI, config.provider);
    const [metadataUrl, owner] = await Promise.all([
        url$1(contract, tokenId, fetchContext),
        contract.ownerOf(tokenId).catch(() => ""),
    ]);
    const metadata = await fetchMetadata(metadataUrl, fetchContext);
    const imageType = urlExtensionType(metadata.image);
    return {
        ...metadata,
        imageType,
        metadataUrl,
        owner,
    };
}

const ETHERS_NOT_FOUND = "Ethers couldn’t be imported. " +
    "Please add the ethers module to your project dependencies, " +
    "or inject it in the Ethers fetcher options.";
async function loadEthers(config) {
    var _a;
    if ((_a = config.ethers) === null || _a === void 0 ? void 0 : _a.Contract) {
        return config;
    }
    try {
        const ethers = await import('@ethersproject/contracts');
        if (!(ethers === null || ethers === void 0 ? void 0 : ethers.Contract)) {
            throw new Error();
        }
        return { ...config, ethers };
    }
    catch (err) {
        throw new Error(ETHERS_NOT_FOUND);
    }
}
async function fetchNftMetadata$1(contractAddress, tokenId, config, fetchContext) {
    if (isDecentralandParcel(contractAddress)) {
        return decentralandParcelMetadata(tokenId);
    }
    if (isDecentralandEstate(contractAddress)) {
        return decentralandEstateMetadata(tokenId);
    }
    if (isCryptoKitties(contractAddress)) {
        return cryptoKittiesMetadata(tokenId, fetchContext);
    }
    const configWithEthersLoaded = await loadEthers(config);
    if (isCryptoPunks(contractAddress)) {
        return cryptoPunksMetadata(tokenId, cryptoPunksImage$1(configWithEthersLoaded));
    }
    if (isMoonCats(contractAddress)) {
        return moonCatsMetadata(tokenId, moonCatsCatId$1(configWithEthersLoaded), fetchContext);
    }
    return fetchStandardNftContractData$1(contractAddress, tokenId, configWithEthersLoaded, fetchContext);
}
function addProxyImage$1(metadata, imageProxy) {
    return metadata.image.startsWith("http")
        ? { ...metadata, image: imageProxy(metadata.image, metadata) }
        : metadata;
}
function ethersFetcher(config) {
    return {
        config,
        async fetchNft(contractAddress, tokenId, fetchContext) {
            if (!isAddress(contractAddress)) {
                throw new Error(`Invalid contract address: ${contractAddress}`);
            }
            const metadata = await fetchNftMetadata$1(contractAddress, tokenId, config, fetchContext);
            return addProxyImage$1(metadata, fetchContext.imageProxy);
        },
    };
}

// Pre encoded method names to avoid embedding a keccak256 library.
// See https://docs.soliditylang.org/en/v0.5.3/abi-spec.html#function-selector-and-argument-encoding
const URI_METHOD_ERC721 = "0xc87b56dd"; // tokenURI(uint256)
const URI_METHOD_ERC1155 = "0x0e89341c"; // uri(uint256)
const OWNER_OF_METHOD_ERC721 = "0x6352211e"; // ownerOf(uint256)
// Utilities adapted from https://github.com/Zoltu/ethereum-abi-encoder/
function uint256Hex(value) {
    let result = "";
    for (let i = 0; i < 32; ++i) {
        result += ("0" + ((value >> BigInt(8 * 32 - i * 8 - 8)) & BigInt(255)).toString(16)).slice(-2);
    }
    return result;
}
function bytesToBigInt(bytes) {
    let value = BigInt(0);
    for (const byte of bytes) {
        value = (value << BigInt(8)) + BigInt(byte);
    }
    return value;
}
function hexToUint8Array(hex) {
    var _a;
    hex = hex.replace(/^0x/, "");
    return new Uint8Array(((_a = hex.match(/.{1,2}/g)) !== null && _a !== void 0 ? _a : []).map((byte) => parseInt(byte, 16)));
}
function decodeString(hex) {
    const data = hexToUint8Array(hex);
    const pointer = Number(bytesToBigInt(data.subarray(0, 32)));
    const length = Number(bytesToBigInt(data.subarray(pointer, pointer + 32)));
    const bytes = data.subarray(pointer + 32, pointer + 32 + length);
    return new TextDecoder().decode(bytes);
}
function decodeAddress(hex) {
    const data = hexToUint8Array(hex);
    const bytes = data.subarray(0, 32);
    const decoded = bytesToBigInt(bytes);
    if (decoded >= BigInt(2) ** BigInt(160))
        throw new Error(`Encoded value is bigger than the largest possible address.  Decoded value: 0x${decoded.toString(16)}.`);
    return `0x${decoded.toString(16)}`;
}
function methodUriErc721(tokenId) {
    return URI_METHOD_ERC721 + uint256Hex(tokenId);
}
function methodUriErc1155(id) {
    return URI_METHOD_ERC1155 + uint256Hex(id);
}
function methodOwnerOfErc721(tokenId) {
    return OWNER_OF_METHOD_ERC721 + uint256Hex(tokenId);
}
function ethCall(ethereum, to, data) {
    return ethereum.request({
        method: "eth_call",
        params: [{ data, to }, "latest"],
    });
}

function cryptoPunksImage(config) {
    return async function cryptoPunksImage(index, method) {
        if (config.ethereum === undefined) {
            throw new Error("No Ethereum provider");
        }
        return ethCall(config.ethereum, method.address, method.methodHash + uint256Hex(BigInt(index))).then(decodeString);
    };
}

function moonCatsCatId(config) {
    return async function moonCatsCatId(tokenId, method) {
        if (config.ethereum === undefined) {
            throw new Error("No Ethereum provider");
        }
        const result = await ethCall(config.ethereum, method.address, method.methodHash + uint256Hex(BigInt(tokenId)));
        return result.slice(0, 12); // 12 = 0x prefix + 5 bytes
    };
}

function uriMethods(tokenId) {
    return [methodUriErc721(BigInt(tokenId)), methodUriErc1155(BigInt(tokenId))];
}
async function url(contractAddress, tokenId, ethereum, fetchContext) {
    const uri = await promiseAny(uriMethods(tokenId).map((method) => ethCall(ethereum, contractAddress, method))).catch((errors) => {
        throw new MultipleErrors("An error occurred while trying to fetch the token URI from the NFT" +
            " contract. See the “errors” property on this error for details.", errors);
    });
    return normalizeTokenUrl(decodeString(uri), tokenId, fetchContext);
}
async function fetchStandardNftContractData(contractAddress, tokenId, { ethereum }, fetchContext) {
    const [metadataUrl, owner] = await Promise.all([
        url(contractAddress, tokenId, ethereum, fetchContext),
        ethCall(ethereum, contractAddress, methodOwnerOfErc721(BigInt(tokenId)))
            .then(decodeAddress)
            .catch(() => ""),
    ]);
    const metadata = await fetchMetadata(metadataUrl, fetchContext);
    const imageType = urlExtensionType(metadata.image);
    return {
        ...metadata,
        imageType,
        metadataUrl,
        owner,
    };
}

async function fetchNftMetadata(contractAddress, tokenId, config, fetchContext) {
    if (isDecentralandParcel(contractAddress)) {
        return decentralandParcelMetadata(tokenId);
    }
    if (isDecentralandEstate(contractAddress)) {
        return decentralandEstateMetadata(tokenId);
    }
    if (isCryptoKitties(contractAddress)) {
        return cryptoKittiesMetadata(tokenId, fetchContext);
    }
    if (isCryptoPunks(contractAddress)) {
        return cryptoPunksMetadata(tokenId, cryptoPunksImage(config));
    }
    if (isMoonCats(contractAddress)) {
        return moonCatsMetadata(tokenId, moonCatsCatId(config), fetchContext);
    }
    return fetchStandardNftContractData(contractAddress, tokenId, config, fetchContext);
}
function addProxyImage(metadata, imageProxy) {
    return metadata.image.startsWith("http")
        ? { ...metadata, image: imageProxy(metadata.image, metadata) }
        : metadata;
}
function normalizeConfig(config) {
    if (!config.ethereum) {
        if (window.ethereum) {
            config.ethereum = window.ethereum;
        }
        else {
            throw new Error("Missing ethereum provider.");
        }
    }
    return config;
}
function ethereumFetcher(config) {
    const _config = normalizeConfig(config);
    return {
        config: _config,
        async fetchNft(contractAddress, tokenId, fetchContext) {
            if (!isAddress(contractAddress)) {
                throw new Error(`Invalid contract address: ${contractAddress}`);
            }
            const metadata = await fetchNftMetadata(contractAddress, tokenId, _config, fetchContext);
            return addProxyImage(metadata, fetchContext.imageProxy);
        },
    };
}

const NFT_METADATA_DEFAULT$1 = {
    name: "",
    description: "",
    image: "",
};
function isFetcherDeclarationEthers(fetcher) {
    return (Array.isArray(fetcher) && fetcher.length == 2 && fetcher[0] === "ethers");
}
function isFetcherDeclarationEthereum(fetcher) {
    return (Array.isArray(fetcher) && fetcher.length == 2 && fetcher[0] === "ethereum");
}
function normalizeFetcher(fetcher) {
    // ethers
    if (isFetcherDeclarationEthers(fetcher)) {
        return ethersFetcher(fetcher[1]);
    }
    // ethereum
    if (isFetcherDeclarationEthereum(fetcher)) {
        return ethereumFetcher(fetcher[1]);
    }
    // custom fetcher (or wrong value)
    return fetcher;
}
const NftContext = createContext(null);
const NftProvider = function NftProvider({ children, fetcher, imageProxy = identity, ipfsUrl = ipfsUrlDefault, jsonProxy = identity, }) {
    if (!fetcher) {
        throw new Error("Please set the fetcher prop on <NftProvider />");
    }
    const context = {
        fetcher: normalizeFetcher(fetcher),
        imageProxy,
        ipfsUrl,
        jsonProxy,
    };
    return (React.createElement(SWRConfig, { value: { provider: () => new Map() } },
        React.createElement(NftContext.Provider, { value: context }, children)));
};
function useNft(contractAddress, tokenId) {
    const context = useContext(NftContext);
    if (context === null) {
        throw new Error("Please wrap your app with <NftProvider />");
    }
    const { fetcher, imageProxy, ipfsUrl, jsonProxy } = context;
    const fetchContext = useMemo(() => ({ imageProxy, ipfsUrl, jsonProxy }), [imageProxy, ipfsUrl, jsonProxy]);
    const fetchNft = useCallback(() => {
        return fetcher
            ? fetcher.fetchNft(contractAddress, tokenId, fetchContext)
            : { ...NFT_METADATA_DEFAULT$1 };
    }, [contractAddress, fetcher, fetchContext, tokenId]);
    const { cache } = useSWRConfig();
    const cached = cache.has(contractAddress + tokenId);
    const result = useSWR(contractAddress + tokenId, fetchNft, {
        revalidateOnMount: !cached,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
    return useMemo(() => {
        const { error, data, mutate } = result;
        const reload = () => mutate()
            .then(() => true)
            .catch(() => false);
        if (error === undefined && data === undefined) {
            return {
                error: undefined,
                loading: true,
                nft: undefined,
                reload,
                status: "loading",
            };
        }
        if (error !== undefined) {
            return {
                error,
                loading: false,
                nft: undefined,
                reload,
                status: "error",
            };
        }
        return {
            error: undefined,
            loading: false,
            nft: data,
            reload,
            status: "done",
        };
    }, [result]);
}

const NFT_METADATA_DEFAULT = {
    name: "",
    description: "",
    image: "",
};
class FetchWrapper {
    constructor(fetcher, options = {}) {
        this.fetcher = this.normalizeFetcher(fetcher);
        this.fetchContext = this.fetchContextFromOptions(options);
    }
    fetchContextFromOptions({ imageProxy, ipfsUrl, jsonProxy, }) {
        return {
            imageProxy: imageProxy !== null && imageProxy !== void 0 ? imageProxy : identity,
            ipfsUrl: ipfsUrl !== null && ipfsUrl !== void 0 ? ipfsUrl : ipfsUrlDefault,
            jsonProxy: jsonProxy !== null && jsonProxy !== void 0 ? jsonProxy : identity,
        };
    }
    normalizeFetcher(fetcher) {
        // default fetcher
        if (!fetcher) {
            return {
                config: {},
                fetchNft: () => Promise.resolve(NFT_METADATA_DEFAULT),
            };
        }
        // ethers
        if (this.isFetcherDeclarationEthers(fetcher)) {
            return ethersFetcher(fetcher[1]);
        }
        // ethereum
        if (this.isFetcherDeclarationEthereum(fetcher)) {
            return ethereumFetcher(fetcher[1]);
        }
        // custom fetcher (or wrong value)
        return fetcher;
    }
    isFetcherDeclarationEthers(fetcher) {
        return (Array.isArray(fetcher) && fetcher.length == 2 && fetcher[0] === "ethers");
    }
    isFetcherDeclarationEthereum(fetcher) {
        return (Array.isArray(fetcher) && fetcher.length == 2 && fetcher[0] === "ethereum");
    }
    async fetchNft(contractAddress, tokenId) {
        return await this.fetcher.fetchNft(contractAddress, tokenId, this.fetchContext);
    }
}

export { FetchWrapper, NftProvider, ethereumFetcher, ethersFetcher, parseNftUrl, useNft };
//# sourceMappingURL=use-nft.esm.js.map
